import React from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { CreditCard, AlertTriangle, Clock, Mail, XCircle, ShieldAlert, FileCheck, Ban } from 'lucide-react';

export default function RefundPage() {
  const effectiveDate = "January 12, 2026";

  return (
    <PublicLayout>
      <Helmet>
        <title>Billing & Refund Policy | Kosha Tax Consultants</title>
        <meta name="description" content="Learn about our billing practices and strict no-refund policy at Kosha Tax Consultants." />
      </Helmet>

      <div className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-4 text-foreground">Billing & Refund Policy</h1>
            <p className="text-center text-muted-foreground mb-8">Effective Date: {effectiveDate}</p>
            
            <Card className="shadow-lg">
              <CardContent className="prose prose-lg dark:prose-invert max-w-none p-6 md:p-8">
                {/* Overview */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Overview</h2>
                  </div>
                  <p>
                    This Billing & Refund Policy applies to all services provided by <strong>Kosha Tax Consultants</strong>, 
                    a DBA of <strong>Annapurna Events LLC</strong> (Texas). Please read this policy carefully before 
                    confirming any draft documents or making payments.
                  </p>
                </section>

                {/* IMPORTANT: No Refund Policy - Prominent Warning */}
                <section className="mb-8">
                  <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-lg p-6 not-prose">
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                      <div>
                        <h2 className="text-xl font-bold text-red-800 dark:text-red-200 mb-3">
                          Important: Strict No-Refund Policy After Draft Confirmation
                        </h2>
                        <p className="text-red-700 dark:text-red-300 mb-4 font-medium">
                          Once a user reviews and confirms the draft version of their tax return or filing documents 
                          and submits payment, the service is considered accepted and no refunds will be provided.
                        </p>
                        <p className="text-red-700 dark:text-red-300 font-medium">
                          No refunds will be issued regardless of filing outcome, processing time, or acceptance 
                          by IRS or state authorities.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* When Charges Occur */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">When Charges Occur</h2>
                  <p>
                    Payment is collected after you review your draft documents and confirm your submission. Your payment 
                    authorizes us to finalize and file your tax return or other requested services.
                  </p>
                  <ul>
                    <li>All payments are processed securely through Stripe</li>
                    <li>We do not store your full credit card details</li>
                    <li>Prices are displayed in U.S. dollars</li>
                    <li>You will receive a confirmation email upon successful payment</li>
                  </ul>
                </section>

                {/* User Responsibility */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <FileCheck className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">User Responsibility</h2>
                  </div>
                  
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 not-prose mb-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                          Review Before You Confirm
                        </p>
                        <p className="text-amber-700 dark:text-amber-300 text-sm">
                          Users are responsible for reviewing draft documents carefully before confirmation. 
                          Once you confirm the draft and complete payment, the service is considered accepted 
                          and final.
                        </p>
                      </div>
                    </div>
                  </div>

                  <p>Before confirming your draft, please ensure:</p>
                  <ul>
                    <li>All personal information is accurate</li>
                    <li>Income and deduction amounts are correct</li>
                    <li>All required documents have been uploaded</li>
                    <li>You understand the service being provided</li>
                  </ul>
                </section>

                {/* No Refund After Confirmation */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Ban className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">No Refunds After Draft Confirmation</h2>
                  </div>
                  
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 not-prose">
                    <div className="flex items-center gap-2 mb-3">
                      <XCircle className="h-5 w-5 text-red-600" />
                      <p className="font-bold text-red-800 dark:text-red-200">Non-Refundable Situations</p>
                    </div>
                    <p className="text-red-700 dark:text-red-300 mb-3">
                      Once you have reviewed and confirmed your draft documents and completed payment, 
                      <strong> no refunds will be provided under any circumstances</strong>, including but not limited to:
                    </p>
                    <ul className="text-sm text-red-700 dark:text-red-300 space-y-2 ml-4">
                      <li>• Tax return or filing rejected by IRS or state authorities</li>
                      <li>• Longer than expected processing times</li>
                      <li>• Lower than expected refund amount</li>
                      <li>• Change of mind after payment</li>
                      <li>• Errors in information you provided</li>
                      <li>• Failure to respond to requests for additional information</li>
                      <li>• Any other reason after draft confirmation and payment</li>
                    </ul>
                  </div>
                </section>

                {/* Limited Exceptions */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Limited Exceptions</h2>
                  <p>
                    Refunds may only be considered in the following rare circumstances, and are granted solely 
                    at our discretion:
                  </p>
                  <ul>
                    <li><strong>Duplicate charges:</strong> If you were charged twice for the same service due to a technical error</li>
                    <li><strong>Before draft confirmation:</strong> If you cancel before reviewing and confirming any draft documents</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-4">
                    Even in these cases, refunds are not guaranteed and will be reviewed on a case-by-case basis.
                  </p>
                </section>

                {/* How to Request a Refund */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Refund Inquiries</h2>
                  </div>
                  <p>
                    If you believe you qualify for one of the limited exceptions above, please contact us with:
                  </p>
                  <ul>
                    <li>Your full name and email address associated with your account</li>
                    <li>Date of payment and transaction amount</li>
                    <li>Detailed explanation of your situation</li>
                    <li>Any relevant supporting documentation</li>
                  </ul>
                  <div className="bg-muted/50 p-4 rounded-lg not-prose mt-4">
                    <p className="font-medium">Contact for Billing Inquiries:</p>
                    <p className="text-muted-foreground">Email: billing@koshatax.com</p>
                    <p className="text-muted-foreground">Subject Line: Billing Inquiry - [Your Name]</p>
                  </div>
                </section>

                {/* Processing Time */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Processing Time</h2>
                  </div>
                  <p>For qualifying refund inquiries only:</p>
                  <ul>
                    <li><strong>Review Period:</strong> We will review your inquiry within 5-7 business days</li>
                    <li><strong>Decision Notification:</strong> You will receive an email with our decision</li>
                    <li><strong>Refund Processing:</strong> If approved, refunds are processed within 5-10 business days</li>
                    <li><strong>Bank Processing:</strong> Additional time may be required for the refund to appear on your statement</li>
                  </ul>
                </section>

                {/* Chargebacks */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Chargebacks & Disputes</h2>
                  <p>
                    By confirming your draft and completing payment, you acknowledge that the service has been accepted. 
                    Filing a chargeback after you have confirmed your draft and received services constitutes a breach 
                    of this agreement.
                  </p>
                  <p>
                    Chargebacks for legitimately provided services may result in:
                  </p>
                  <ul>
                    <li>Immediate account suspension</li>
                    <li>Collection action for the owed amount</li>
                    <li>Reporting to appropriate authorities if fraud is suspected</li>
                  </ul>
                </section>

                {/* Summary Box */}
                <section className="mb-8">
                  <div className="bg-slate-100 dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 rounded-lg p-6 not-prose">
                    <h3 className="font-bold text-lg mb-3">Summary</h3>
                    <p className="text-sm mb-2">
                      <strong>Confirmed draft + payment = No refunds.</strong>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Please review all draft documents carefully before confirming. Once you confirm and pay, 
                      the service is considered accepted and no refunds will be issued.
                    </p>
                  </div>
                </section>

                {/* Contact */}
                <section>
                  <h2 className="text-2xl font-semibold">Questions?</h2>
                  <p>If you have any questions about our Billing & Refund Policy, please contact us before making a purchase:</p>
                  <div className="bg-muted/50 p-4 rounded-lg not-prose mt-4">
                    <p className="font-medium">Kosha Tax Consultants</p>
                    <p className="text-muted-foreground">A DBA of Annapurna Events LLC (Texas)</p>
                    <p className="text-muted-foreground">Email: billing@koshatax.com</p>
                  </div>
                </section>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
